import React, { useEffect, useState, useMemo } from "react";
import "./styles/Reports.css";
import { apiService } from "./services/apiService";
import * as XLSX from "xlsx";
import { 
  FaDownload, 
  FaFileExcel, 
  FaEnvelope, 
  FaSort, 
  FaSortUp, 
  FaSortDown, 
  FaPlus, 
  FaSearch, 
  FaEraser,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle
} from "react-icons/fa";

function StudentEnquiryComponent({ onBack }) {
  // Helper functions for default date calculation
  const getTodayDate = () => new Date().toISOString().split("T")[0];
  const getFirstDayOfMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split("T")[0];
  };

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    primaryContact: "",
    alternateContact: "",
    email: "",
    enquiryDate: getTodayDate(),
    address: "",
    purpose: "",
  });

  // Filter & Data States
  const [fromDate, setFromDate] = useState(getFirstDayOfMonth());
  const [toDate, setToDate] = useState(getTodayDate());
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailing, setEmailing] = useState(false);

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // Custom Modal Popup State
  const [modal, setModal] = useState({
    show: false,
    title: "",
    message: "",
    type: "warning" // 'warning', 'success', 'error'
  });

  const currentTimestamp = new Date().toLocaleString("en-GB", { hour12: true });

  const showPopup = (title, message, type = "warning") => {
    setModal({ show: true, title, message, type });
  };

  const closePopup = () => {
    setModal({ ...modal, show: false });
  };

  // Check if all form fields are non-empty
  const isFormValid = useMemo(() => {
    return (
      formData.name.trim() !== "" &&
      formData.primaryContact.trim() !== "" &&
      formData.alternateContact.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.address.trim() !== "" &&
      formData.purpose.trim() !== "" &&
      formData.enquiryDate.trim() !== ""
    );
  }, [formData]);

  // Fetch Enquiries
  const fetchEnquiries = async (overrideFrom = fromDate, overrideTo = toDate) => {
    setLoading(true);
    try {
      const data = await apiService.getEnquiries({ 
        fromDate: overrideFrom, 
        toDate: overrideTo 
      });
      setEnquiries(data || []);
    } catch (err) {
      console.error("Error fetching enquiries:", err);
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries(getFirstDayOfMonth(), getTodayDate());
  }, []);

  // Handle Form Inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // For contact numbers, allow typing only digits up to 10 chars
    if (name === "primaryContact" || name === "alternateContact") {
      const cleanValue = value.replace(/\D/g, "").slice(0, 10);
      setFormData({ ...formData, [name]: cleanValue });
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  // Input Validation Rules
  const validateForm = () => {
    const phoneRegex = /^[0-9]{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Name Validation
    if (!formData.name.trim()) {
      showPopup("Validation Error", "Please enter the student's name.", "warning");
      return false;
    }

    // Primary Contact Validation (Mandatory 10 Digits)
    if (!phoneRegex.test(formData.primaryContact)) {
      showPopup("Invalid Primary Contact", "Primary contact number must be exactly 10 digits.", "warning");
      return false;
    }

    // Alternate Contact Validation (Must be 10 Digits)
    if (!phoneRegex.test(formData.alternateContact)) {
      showPopup("Invalid Alternate Contact", "Alternate contact number must be exactly 10 digits.", "warning");
      return false;
    }

    // Email Validation (Must be valid format)
    if (!emailRegex.test(formData.email)) {
      showPopup("Invalid Email Address", "Please enter a valid email address format (e.g. name@example.com).", "warning");
      return false;
    }

    return true;
  };

  // Submit Short Form
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await apiService.createEnquiry(formData);
      showPopup("Success", "Student enquiry registered successfully!", "success");
      setFormData({
        name: "",
        primaryContact: "",
        alternateContact: "",
        email: "",
        enquiryDate: getTodayDate(),
        address: "",
        purpose: "",
      });
      await fetchEnquiries();
    } catch (err) {
      console.error(err);
      showPopup("Submission Failed", "Failed to save enquiry details. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Clear Date Filters
  const handleClearFilter = () => {
    const defaultFrom = getFirstDayOfMonth();
    const defaultTo = getTodayDate();
    setFromDate(defaultFrom);
    setToDate(defaultTo);
    fetchEnquiries(defaultFrom, defaultTo);
  };

  // Sorting Handler
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Memoized Sorting Logic
  const sortedEnquiries = useMemo(() => {
    let sortableItems = [...enquiries];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue === undefined || aValue === null) return 1;
        if (bValue === undefined || bValue === null) return -1;

        if (sortConfig.key === "enquiryDate") {
          return sortConfig.direction === "asc"
            ? new Date(aValue) - new Date(bValue)
            : new Date(bValue) - new Date(aValue);
        }

        const stringA = String(aValue).toLowerCase();
        const stringB = String(bValue).toLowerCase();

        if (stringA < stringB) return sortConfig.direction === "asc" ? -1 : 1;
        if (stringA > stringB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [enquiries, sortConfig]);

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort style={{ marginLeft: "6px", opacity: 0.4 }} />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp style={{ marginLeft: "6px", color: "#007bff" }} />
    ) : (
      <FaSortDown style={{ marginLeft: "6px", color: "#007bff" }} />
    );
  };

  // Export PDF Blob - Enquiry Date shifted after Primary Contact
  const generatePDFBlob = async () => {
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("Student Enquiry Report", 14, 15);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated On: ${currentTimestamp}`, 14, 22);

    const tableBody = sortedEnquiries.map((e, i) => [
      i + 1,
      e.name || "",
      e.primaryContact || "",
      e.enquiryDate || "—",
      e.alternateContact || "—",
      e.email || "—",
      e.address || "—",
      e.purpose || "—",
    ]);

    tableBody.push(["", "", "", "", "", "", "Total Enquiries:", `${sortedEnquiries.length}`]);

    autoTable(doc, {
      startY: 28,
      head: [["#", "Student Name", "Primary Contact", "Enquiry Date", "Alternate Contact", "Email", "Address", "Purpose"]],
      body: tableBody,
    });

    return doc.output("blob");
  };

  // Export Excel Data Format - Enquiry Date shifted after Primary Contact
  const prepareExcelData = () => {
    const rows = sortedEnquiries.map((e, i) => ({
      "#": i + 1,
      "Student Name": e.name,
      "Primary Contact": e.primaryContact,
      "Enquiry Date": e.enquiryDate,
      "Alternate Contact": e.alternateContact || "N/A",
      "Email": e.email || "N/A",
      "Address": e.address || "N/A",
      "Purpose": e.purpose || "N/A",
    }));

    rows.push({
      "#": "",
      "Student Name": "",
      "Primary Contact": "",
      "Enquiry Date": "",
      "Alternate Contact": "",
      "Email": "",
      "Address": "Total Enquiries:",
      "Purpose": sortedEnquiries.length,
    });

    return rows;
  };

  const handleDownloadXls = () => {
    const worksheet = XLSX.utils.json_to_sheet(prepareExcelData());
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");
    XLSX.writeFile(workbook, "Student-Enquiries-Report.xlsx");
  };

  const handleEmailBoth = async () => {
    setEmailing(true);
    try {
      const pdfBlob = await generatePDFBlob();
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(prepareExcelData()), "Enquiries");
      const xlsBlob = new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fd = new FormData();
      fd.append("pdf", pdfBlob, "EnquiryReport.pdf");
      fd.append("xlsx", xlsBlob, "EnquiryReport.xlsx");

      await apiService.emailEnquiryReport(fd);
      showPopup("Success", "Email report dispatched successfully!", "success");
    } catch (err) {
      console.error(err);
      showPopup("Email Failed", "Failed to dispatch email. Please check server connection.", "error");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="rp-page">
      {/* Header Bar */}
      <div className="rp-header-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 className="rp-title" style={{ margin: 0 }}>Student Enquiry Management</h2>
          <div className="rp-timestamp" style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
            Generated On: <b>{currentTimestamp}</b>
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {enquiries.length > 0 && (
            <>
              <button
                onClick={() =>
                  generatePDFBlob().then((b) => {
                    const url = URL.createObjectURL(b);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "Enquiry-Report.pdf";
                    a.click();
                  })
                }
                className="rp-btn-back"
              >
                <FaDownload /> PDF
              </button>
              <button onClick={handleDownloadXls} className="rp-btn-back">
                <FaFileExcel /> Excel
              </button>
              <button onClick={handleEmailBoth} disabled={emailing} className="rp-btn-back">
                <FaEnvelope /> {emailing ? "Sending..." : "Email Both"}
              </button>
            </>
          )}
          {onBack && (
            <button onClick={onBack} className="rp-btn-back">
              Back To Dashboard
            </button>
          )}
        </div>
      </div>

      {/* Short Form Card */}
      <div className="rp-card" style={{ padding: "20px", marginBottom: "20px" }}>
        <h3 style={{ margin: "0 0 16px 0", fontSize: "16px", color: "#333" }}>New Student Enquiry</h3>
        <form onSubmit={handleFormSubmit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
          <input
            type="text"
            name="name"
            placeholder="Student Name *"
            value={formData.name}
            onChange={handleInputChange}
            className="rp-input"
          />
          <input
            type="text"
            name="primaryContact"
            placeholder="Primary Contact (10 Digits) *"
            value={formData.primaryContact}
            onChange={handleInputChange}
            maxLength={10}
            className="rp-input"
          />
          <input
            type="text"
            name="alternateContact"
            placeholder="Alternate Contact (10 Digits)"
            value={formData.alternateContact}
            onChange={handleInputChange}
            maxLength={10}
            className="rp-input"
          />
          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleInputChange}
            className="rp-input"
          />
          <input
            type="text"
            name="address"
            placeholder="Address"
            value={formData.address}
            onChange={handleInputChange}
            className="rp-input"
          />
          <input
            type="text"
            name="purpose"
            placeholder="Purpose"
            value={formData.purpose}
            onChange={handleInputChange}
            className="rp-input"
          />
          <input
            type="date"
            name="enquiryDate"
            value={formData.enquiryDate}
            onChange={handleInputChange}
            required
            className="rp-input"
          />
          {/* Submit Button enabled only when all fields are filled */}
          <button 
            type="submit" 
            disabled={submitting || !isFormValid} 
            className="rp-btn-back" 
            style={{ 
              background: !isFormValid ? "#cccccc" : "#007bff", 
              color: "#fff", 
              borderColor: !isFormValid ? "#cccccc" : "#007bff",
              cursor: !isFormValid ? "not-allowed" : "pointer"
            }}
          >
            <FaPlus /> {submitting ? "Saving..." : "Add Enquiry"}
          </button>
        </form>
      </div>

      {/* Grid Filter Bar & Report Card */}
      <div className="rp-card">
        {/* Section Heading */}
        <div style={{ padding: "16px 16px 8px 16px", borderBottom: "1px solid #f0f0f0" }}>
          <h3 style={{ margin: 0, fontSize: "16px", color: "#333" }}>Enquiries Report</h3>
        </div>

        {/* Filter Bar with Total Count */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", gap: "16px", background: "#fafafa", flexWrap: "wrap", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ fontWeight: "600", fontSize: "13px", color: "#444" }}>From Date:</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rp-input" />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ fontWeight: "600", fontSize: "13px", color: "#444" }}>To Date:</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rp-input" />
            </div>
            <button onClick={() => fetchEnquiries(fromDate, toDate)} className="rp-btn-back" style={{ padding: "6px 12px" }}>
              <FaSearch /> Search
            </button>
            <button onClick={handleClearFilter} style={{ background: "transparent", border: "none", color: "#d32f2f", cursor: "pointer", fontSize: "13px", fontWeight: "600", textDecoration: "underline", display: "flex", alignItems: "center", gap: "4px" }}>
              <FaEraser /> Clear Filter
            </button>
          </div>

          <div style={{ fontSize: "13px", fontWeight: "600", color: "#333", background: "#e8f5e9", padding: "6px 12px", borderRadius: "4px", border: "1px solid #c8e6c9" }}>
            Total Enquiries: <span style={{ color: "#2e7d32", fontSize: "14px", fontWeight: "700" }}>{sortedEnquiries.length}</span>
          </div>
        </div>

        {/* Data Grid Table */}
        <div className="rp-table-wrapper">
          <table className="rp-table">
            <thead>
              <tr>
                <th style={{ userSelect: "none", width: "50px" }}>#</th>
                <th onClick={() => handleSort("name")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Name {renderSortIcon("name")}
                </th>
                <th onClick={() => handleSort("primaryContact")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Primary Contact {renderSortIcon("primaryContact")}
                </th>
                {/* Enquiry Date shifted directly after Primary Contact */}
                <th onClick={() => handleSort("enquiryDate")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Enquiry Date {renderSortIcon("enquiryDate")}
                </th>
                <th onClick={() => handleSort("alternateContact")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Alternate Contact {renderSortIcon("alternateContact")}
                </th>
                <th onClick={() => handleSort("email")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Email {renderSortIcon("email")}
                </th>
                <th onClick={() => handleSort("address")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Address {renderSortIcon("address")}
                </th>
                <th onClick={() => handleSort("purpose")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Purpose {renderSortIcon("purpose")}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>Loading enquiries...</td>
                </tr>
              ) : sortedEnquiries.length > 0 ? (
                sortedEnquiries.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{index + 1}</td>
                    <td className="rp-td-name">{item.name}</td>
                    <td>{item.primaryContact}</td>
                    {/* Enquiry Date data cell placed after Primary Contact */}
                    <td>{item.enquiryDate}</td>
                    <td>{item.alternateContact || "—"}</td>
                    <td>{item.email || "—"}</td>
                    <td>{item.address || "—"}</td>
                    <td>{item.purpose || "—"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                    No student enquiries recorded for the selected criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Custom Modal Popup */}
      {modal.show && (
        <div style={modalOverlayStyle}>
          <div style={modalContainerStyle}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>
              {modal.type === "success" && <FaCheckCircle style={{ color: "#2e7d32" }} />}
              {modal.type === "error" && <FaTimesCircle style={{ color: "#d32f2f" }} />}
              {modal.type === "warning" && <FaExclamationTriangle style={{ color: "#f57c00" }} />}
            </div>
            <h3 style={{ margin: "0 0 8px 0", color: "#222", fontSize: "18px" }}>{modal.title}</h3>
            <p style={{ margin: "0 0 20px 0", color: "#555", fontSize: "14px", lineHeight: "1.4" }}>
              {modal.message}
            </p>
            <button 
              onClick={closePopup}
              style={{
                background: modal.type === "success" ? "#2e7d32" : modal.type === "error" ? "#d32f2f" : "#f57c00",
                color: "#fff",
                border: "none",
                padding: "10px 24px",
                borderRadius: "6px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                transition: "all 0.2s"
              }}
            >
              Understand & Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Inline Styles for Modal
const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.45)",
  backdropFilter: "blur(4px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
  animation: "fadeIn 0.2s ease-out",
};

const modalContainerStyle = {
  background: "#ffffff",
  borderRadius: "12px",
  padding: "24px 28px",
  maxWidth: "380px",
  width: "90%",
  textAlign: "center",
  boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
  border: "1px solid #eee",
};

export default StudentEnquiryComponent;